/**
 * Every email a pilot partner reviews, and each partner's decision on it.
 *
 * New York Life requires that every agent-facing and customer-facing communication is reviewed
 * and approved before use, and sent a matrix that marks each one As Is, Modify, Disable or
 * Enable. This file is that matrix in code:
 *
 * - `PILOT_COMMUNICATIONS` lists the communications themselves, one entry per matrix row, keyed by
 *   a stable name and carrying the partner's row number so the two can be read side by side.
 * - `NYL_COMMUNICATION_POLICY` records New York Life's decision on each row, whether that decision
 *   is final (`approval`), whether it waits for the pilot's client phase, and, for the rows NYL asked
 *   us to reword, the replacement text.
 *
 * The program in `pilotPrograms.ts` points at its policy, and `decidePilotCommunication` there turns
 * a policy row into "send / hold". Every server path that sends one of these emails, or starts one
 * of the Customer.io campaigns, asks that function first (see `server/src/util/pilotCommunications.ts`).
 *
 * Two kinds of row cannot be enforced from this repository:
 * - `triggeredFrom: 'customer-io'` rows are started by a Customer.io segment, not by an event we
 *   send. They are held by excluding the `pilot_program` person attribute inside Customer.io.
 * - Customer.io wording lives in Customer.io, so a Customer.io row marked `modify` stays held until
 *   someone edits the campaign there AND the partner approves it, at which point `approval` flips.
 *
 * Changing a row's `approval` to `approved` is the act of recording the partner's sign-off. Do it
 * only with that sign-off in hand, and change the replacement text and the approval together.
 */

export type PilotCommunicationKey =
  | 'client-results-link'
  | 'client-intake-invite'
  | 'client-intake-submitted'
  | 'client-results-reminder'
  | 'client-intake-reminder'
  | 'partner-link-request'
  | 'partner-link-accepted'
  | 'advisor-meeting-request'
  | 'advisor-intake-submitted'
  | 'advisor-pdf-report'
  | 'advisor-two-pager'
  | 'advisor-free-trial-welcome'
  | 'advisor-subscription-time-remaining'
  | 'seat-invitation'
  | 'seat-invitation-accepted'
  | 'seat-invitation-declined'
  | 'organization-invitation'
  | 'organization-invitation-accepted'
  | 'organization-invitation-declined'
  | 'client-shared'
  | 'distribution-partner-invitation'
  | 'sponsorship-invitation'
  | 'sponsorship-cancellation'
  | 'campaign-first-client-prompt'
  | 'campaign-first-client-day-3'
  | 'campaign-first-client-day-4'
  | 'campaign-intake-pending'
  | 'campaign-plan-ready'
  | 'campaign-client-intake-guide'
  | 'campaign-ten-minute-tracker'
  | 'campaign-ten-plans-gift'
  | 'campaign-email-connection-reminder'
  | 'campaign-weekly-digest'
  | 'campaign-cancellation-confirmation'
  | 'campaign-post-cancellation'
  | 'campaign-last-chance'
  | 'campaign-extended-last-chance';

export type PilotCommunicationChannel = 'direct-email' | 'customer-io';

export type PilotCommunicationAudience = 'client' | 'client-partner' | 'advisor' | 'external';

export interface PilotCommunication {
  /** Row number in the partner's communications matrix. */
  matrixRow: string;
  name: string;
  audience: PilotCommunicationAudience;
  channel: PilotCommunicationChannel;
  /** Subject line as the partner reviewed it. */
  subject: string;
  /**
   * `code` when this repository sends the email or the event that starts it, so it can be held
   * here. `customer-io` when a Customer.io segment starts it and only Customer.io can hold it.
   */
  triggeredFrom: 'code' | 'customer-io';
}

export const PILOT_COMMUNICATIONS: Readonly<Record<PilotCommunicationKey, PilotCommunication>> = {
  'client-results-link': {
    matrixRow: '1.1',
    name: 'Personalized LTC results / access link',
    audience: 'client',
    channel: 'direct-email',
    subject: '{clientFirstName}, View Your Personalized Long-Term Care Results',
    triggeredFrom: 'code',
  },
  'client-intake-invite': {
    matrixRow: '1.2',
    name: 'Start your LTC plan (intake form invite)',
    audience: 'client',
    channel: 'direct-email',
    subject: "Let's start your LTC plan - takes less than 10 minutes",
    triggeredFrom: 'code',
  },
  'client-intake-submitted': {
    matrixRow: '1.3',
    name: 'Intake survey submitted confirmation',
    audience: 'client',
    channel: 'direct-email',
    subject: 'You successfully submitted your long-term care survey',
    triggeredFrom: 'code',
  },
  'client-results-reminder': {
    matrixRow: '1.4',
    name: 'View your predictions now reminder',
    audience: 'client',
    channel: 'direct-email',
    subject: 'Reminder: You can view your long-term care predictions now!',
    triggeredFrom: 'code',
  },
  'client-intake-reminder': {
    matrixRow: '1.5',
    name: 'Fill out your intake form reminder',
    audience: 'client',
    channel: 'direct-email',
    subject: 'Help me help you with LTC planning',
    triggeredFrom: 'code',
  },
  'partner-link-request': {
    matrixRow: '1.6',
    name: 'Partner link request',
    audience: 'client-partner',
    channel: 'direct-email',
    subject: '{clientFirstName} {clientLastName} would like to link accounts',
    triggeredFrom: 'code',
  },
  'partner-link-accepted': {
    matrixRow: '1.7',
    name: 'Partner link confirmation',
    audience: 'client',
    channel: 'direct-email',
    subject: '{partnerFirstName} {partnerLastName} has accepted your link request',
    triggeredFrom: 'code',
  },
  'advisor-meeting-request': {
    matrixRow: '2.1',
    name: 'Meeting request',
    audience: 'advisor',
    channel: 'direct-email',
    subject: '{clientFullName} has requested a meeting with you via Waterlily Planning',
    triggeredFrom: 'code',
  },
  'advisor-intake-submitted': {
    matrixRow: '2.2',
    name: 'Client submitted their intake form',
    audience: 'advisor',
    channel: 'direct-email',
    subject: '{clientFirstName} {clientLastName} Has Submitted Their Intake Form',
    triggeredFrom: 'code',
  },
  'advisor-pdf-report': {
    matrixRow: '2.3',
    name: 'LTC planning report (PDF)',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'LTC Planning Report - {clientName}',
    triggeredFrom: 'code',
  },
  'advisor-two-pager': {
    matrixRow: '2.4',
    name: 'Two-pager to share with your team',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'Your Waterlily two-pager to share with your team',
    triggeredFrom: 'code',
  },
  'advisor-free-trial-welcome': {
    matrixRow: '2.5',
    name: 'Free-trial welcome',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'Your first day with Waterlily',
    triggeredFrom: 'code',
  },
  'advisor-subscription-time-remaining': {
    matrixRow: '2.6',
    name: 'Subscription cycle time-remaining reminder',
    audience: 'advisor',
    channel: 'direct-email',
    subject: '{timeRemaining} Left in Your Subscription Cycle',
    triggeredFrom: 'code',
  },
  'seat-invitation': {
    matrixRow: '2.7',
    name: 'Seat invitation',
    audience: 'advisor',
    channel: 'direct-email',
    subject: "You've Been Invited to Join Waterlily",
    triggeredFrom: 'code',
  },
  'seat-invitation-accepted': {
    matrixRow: '2.8',
    name: 'Seat invitation accepted',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'Your Waterlily Invitation Was Accepted',
    triggeredFrom: 'code',
  },
  'seat-invitation-declined': {
    matrixRow: '2.9',
    name: 'Seat invitation declined',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'Waterlily Invitation Declined',
    triggeredFrom: 'code',
  },
  'organization-invitation': {
    matrixRow: '2.10',
    name: 'Organization invitation',
    audience: 'advisor',
    channel: 'direct-email',
    subject: "You've Been Invited to Join {organization name / 'an Organization'} on Waterlily",
    triggeredFrom: 'code',
  },
  'organization-invitation-accepted': {
    matrixRow: '2.11',
    name: 'Organization invitation accepted',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'Your Organization Invitation Was Accepted',
    triggeredFrom: 'code',
  },
  'organization-invitation-declined': {
    matrixRow: '2.12',
    name: 'Organization invitation declined',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'Organization Invitation Declined',
    triggeredFrom: 'code',
  },
  'client-shared': {
    matrixRow: '2.13',
    name: 'Client shared with you',
    audience: 'advisor',
    channel: 'direct-email',
    subject: '{ownerName} shared {clientName} with you on Waterlily',
    triggeredFrom: 'code',
  },
  'distribution-partner-invitation': {
    matrixRow: '2.14',
    name: 'Distribution partner invitation',
    audience: 'external',
    channel: 'direct-email',
    subject: '{advisorName} invited you to collaborate on Waterlily',
    triggeredFrom: 'code',
  },
  'sponsorship-invitation': {
    matrixRow: '2.15',
    name: 'Sponsorship invitation',
    audience: 'advisor',
    channel: 'direct-email',
    subject: "You've Been Invited to A Sponsorship",
    triggeredFrom: 'code',
  },
  'sponsorship-cancellation': {
    matrixRow: '2.16',
    name: 'Sponsorship cancellation',
    audience: 'advisor',
    channel: 'direct-email',
    subject: 'Your Waterlily Sponsorship Has Been Canceled',
    triggeredFrom: 'code',
  },
  'campaign-first-client-prompt': {
    matrixRow: '3.1.1',
    name: 'First-client invite prompt',
    audience: 'advisor',
    channel: 'customer-io',
    subject: "Let's get your first client plan started (60-second setup)",
    triggeredFrom: 'code',
  },
  'campaign-first-client-day-3': {
    matrixRow: '3.1.2',
    name: 'First-client reminder - Day 3',
    audience: 'advisor',
    channel: 'customer-io',
    subject: "Don't leave $1,200,000 of LTC risk unplanned",
    triggeredFrom: 'code',
  },
  'campaign-first-client-day-4': {
    matrixRow: '3.1.3',
    name: 'First-client reminder - Day 4',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'A client-ready script for your LTC planning',
    triggeredFrom: 'code',
  },
  'campaign-intake-pending': {
    matrixRow: '3.2',
    name: 'Intake-pending advisor reminder',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'A follow-up for {{ event.client_name }} (intake pending)',
    triggeredFrom: 'code',
  },
  'campaign-plan-ready': {
    matrixRow: '3.3',
    name: 'Client plan ready / book review meeting',
    audience: 'advisor',
    channel: 'customer-io',
    subject: "{{event.client_name}}'s LTC plan is ready to review!",
    triggeredFrom: 'code',
  },
  'campaign-client-intake-guide': {
    matrixRow: '3.4',
    name: 'Client Intake Guide / What to Expect Next',
    audience: 'client',
    channel: 'customer-io',
    subject: 'Your Waterlily Planning Guide - What to Expect Next',
    triggeredFrom: 'code',
  },
  'campaign-ten-minute-tracker': {
    matrixRow: '3.5',
    name: '10-minute onboarding tracker',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'How was your LTC planning session with {{customer.client_name}}?',
    triggeredFrom: 'code',
  },
  'campaign-ten-plans-gift': {
    matrixRow: '3.6',
    name: '10 LTC plans reached',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'We want to send you a gift for building your 10th LTC plan in Waterlily!',
    triggeredFrom: 'code',
  },
  'campaign-email-connection-reminder': {
    matrixRow: '3.7',
    name: 'Google/Microsoft account connection reminder',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'We want to send you a gift for building your 10th LTC plan in Waterlily!',
    triggeredFrom: 'customer-io',
  },
  'campaign-weekly-digest': {
    matrixRow: '3.8',
    name: 'Weekly Digest',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'Weekly Digest',
    triggeredFrom: 'customer-io',
  },
  'campaign-cancellation-confirmation': {
    matrixRow: '3.9.1',
    name: 'Cancellation confirmation',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'Waterlily Cancellation Request',
    triggeredFrom: 'code',
  },
  'campaign-post-cancellation': {
    matrixRow: '3.9.2',
    name: 'Post-cancellation follow-up',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'You still have access, make the most of it',
    triggeredFrom: 'code',
  },
  'campaign-last-chance': {
    matrixRow: '3.9.3',
    name: 'Last chance before access ends',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'Last Chance before your Waterlily access ends',
    triggeredFrom: 'code',
  },
  'campaign-extended-last-chance': {
    matrixRow: '3.9.4',
    name: 'Extended cancellation / last chance',
    audience: 'advisor',
    channel: 'customer-io',
    subject: 'Last Chance before your Waterlily access ends',
    triggeredFrom: 'code',
  },
};

export type PilotCommunicationApproval = 'approved' | 'awaiting-review';

/** A row the partner allows, as written (`as-is`, `enable`) or after changes (`modify`). */
export interface PilotCommunicationSendRule {
  disposition: 'as-is' | 'modify' | 'enable';
  /** Only `approved` rows are sent. Anything else is held until the partner signs off. */
  approval: PilotCommunicationApproval;
  /** What sign-off is still outstanding. Set exactly when `approval` is `awaiting-review`. */
  awaiting?: string;
  /** The partner placed this row in the client phase, so it is held during the advisor-only period. */
  waitsForClientUse: boolean;
  /**
   * The approved body. When set it replaces both the default body and anything the sender typed,
   * because the partner approves wording, and wording the sender edits is wording nobody approved.
   */
  lockedBody?: string;
  /** The partner's instruction for this row, verbatim from the matrix. */
  partnerNote?: string;
}

export interface PilotCommunicationDisableRule {
  disposition: 'disable';
  partnerNote?: string;
}

export type PilotCommunicationRule = PilotCommunicationSendRule | PilotCommunicationDisableRule;

export type PilotCommunicationPolicy = Readonly<
  Record<PilotCommunicationKey, PilotCommunicationRule>
>;

/**
 * New York Life wording for the intake invite (row 1.2), without the greeting that the sender adds
 * when the client's first name is known.
 */
export const NYL_CLIENT_INTAKE_INVITE_BODY = `Planning for the possibility of a long-term care need is one part of preparing for retirement, and I'd like to help you think it through.

I use Waterlily to build personalized long-term care planning scenarios. It uses your answers to estimate possible future care needs and costs, and then we can review different ways those costs could be funded and talk through which options may fit your situation.

The first step is to complete the intake form below (it's about 5 minutes long). Once it's done, we can review your planning scenarios together.`;

/** New York Life wording for the results reminder (row 1.4). */
export const NYL_CLIENT_RESULTS_REMINDER_BODY = `Your personalized long-term care planning results are ready to view.

By filling out the intake form, you already did half the work. Next we'll walk through your care and cost scenarios together as part of your planning.`;

/** New York Life wording for the intake reminder (row 1.5). */
export const NYL_CLIENT_INTAKE_REMINDER_BODY = `Did you get my earlier invite to Waterlily to build your long-term care plan together?

Completing this intake form gives us a personalized estimate of your possible future long-term care needs and costs, which we can use as a starting point for planning.

From there, we can review different planning scenarios and funding options for your household together.

It takes less than 10 minutes, just click below to start.`;

const AWAITING_CUSTOMER_IO_WORDING =
  'Campaign wording edited in Customer.io, then NYL SMRU review of the edited campaign';

/** New York Life's decision on every row of its communications matrix. */
export const NYL_COMMUNICATION_POLICY: PilotCommunicationPolicy = {
  'client-results-link': { disposition: 'as-is', approval: 'approved', waitsForClientUse: true },
  'client-intake-invite': {
    disposition: 'modify',
    approval: 'approved',
    waitsForClientUse: true,
    lockedBody: NYL_CLIENT_INTAKE_INVITE_BODY,
    partnerNote:
      'Remove refrences to “devastating,” to avoid alarmist/urgency/fear framing. Use neutral educational planning/scenario language.',
  },
  'client-intake-submitted': {
    disposition: 'as-is',
    approval: 'approved',
    waitsForClientUse: true,
  },
  'client-results-reminder': {
    disposition: 'modify',
    approval: 'approved',
    waitsForClientUse: true,
    lockedBody: NYL_CLIENT_RESULTS_REMINDER_BODY,
    partnerNote: 'Remove “Don’t wait until it’s too late” and similar urgency/fear framing',
  },
  'client-intake-reminder': {
    disposition: 'modify',
    approval: 'approved',
    waitsForClientUse: true,
    lockedBody: NYL_CLIENT_INTAKE_REMINDER_BODY,
    partnerNote:
      'Remove reference to "six/seven-figure claims" and “produce the highest expected ROI for your projections.  Its one of the most valuable plans we can build together."',
  },
  'partner-link-request': { disposition: 'as-is', approval: 'approved', waitsForClientUse: true },
  'partner-link-accepted': { disposition: 'as-is', approval: 'approved', waitsForClientUse: true },
  'advisor-meeting-request': {
    disposition: 'as-is',
    approval: 'approved',
    waitsForClientUse: true,
  },
  'advisor-intake-submitted': {
    disposition: 'as-is',
    approval: 'approved',
    waitsForClientUse: true,
  },
  'advisor-pdf-report': {
    disposition: 'as-is',
    approval: 'awaiting-review',
    awaiting: 'NYL approval of the attached PDF report output and disclosures',
    waitsForClientUse: true,
    partnerNote:
      'Email wrapper may remain as written. The attached PDF must be the NYL-approved output/disclosure version, and advisor retention obligations still apply.',
  },
  'advisor-two-pager': {
    disposition: 'disable',
    partnerNote: 'Can discuss potentially keeping but would need to have 2-pager reviewed.',
  },
  'advisor-free-trial-welcome': {
    disposition: 'disable',
    partnerNote: 'NYL pilot is not an advisor free-trial/subscription offer.',
  },
  'advisor-subscription-time-remaining': {
    disposition: 'disable',
    partnerNote:
      'Subscription-cycle and complimentary-month messaging does not apply to the NYL-sponsored pilot.',
  },
  'seat-invitation': {
    disposition: 'disable',
    partnerNote:
      'Disable advisor-to-advisor seat invitation/sponsorship workflow. Pilot access should be controlled by the NYL-approved participant roster/onboarding process.',
  },
  'seat-invitation-accepted': {
    disposition: 'disable',
    partnerNote:
      'Disable because the underlying seat-invitation workflow is disabled for the NYL pilot.',
  },
  'seat-invitation-declined': {
    disposition: 'disable',
    partnerNote:
      'Disable because the underlying seat-invitation workflow is disabled for the NYL pilot.',
  },
  'organization-invitation': {
    disposition: 'enable',
    approval: 'approved',
    waitsForClientUse: false,
    partnerNote: 'ENABLE - we still want them to receive it as it is the Welcome Email.',
  },
  'organization-invitation-accepted': {
    disposition: 'disable',
    partnerNote: 'Disable unless determined part of pilot setup - see 2.10',
  },
  'organization-invitation-declined': {
    disposition: 'disable',
    partnerNote: 'Disable unless determined part of pilot setup - see 2.10',
  },
  'client-shared': {
    disposition: 'disable',
    partnerNote: 'Disable advisor-to-advisor client sharing for the pilot',
  },
  'distribution-partner-invitation': {
    disposition: 'disable',
    partnerNote:
      'Disable for the pilot. It introduces external distribution-partner collaboration, quoting and application activity that is outside the approved educational/planning-only pilot scope.',
  },
  'sponsorship-invitation': {
    disposition: 'disable',
    partnerNote: 'Disable advisor sponsorship invitation functionality/messages for the NYL pilot.',
  },
  'sponsorship-cancellation': {
    disposition: 'disable',
    partnerNote: 'Disable because sponsorship functionality is not part of the NYL pilot.',
  },
  'campaign-first-client-prompt': {
    disposition: 'modify',
    approval: 'awaiting-review',
    awaiting: AWAITING_CUSTOMER_IO_WORDING,
    waitsForClientUse: true,
    partnerNote:
      'Must not prompt client invitations during the first 30 days. Enable only when NYL authorizes client use; all variants must be in the SMRU submission.',
  },
  'campaign-first-client-day-3': {
    disposition: 'modify',
    approval: 'awaiting-review',
    awaiting: AWAITING_CUSTOMER_IO_WORDING,
    waitsForClientUse: true,
    partnerNote:
      'Do not send during the advisor-only period. Remove reference to $1.2M and 6–7 figure loss claims and fear-based framing.',
  },
  'campaign-first-client-day-4': {
    disposition: 'modify',
    approval: 'awaiting-review',
    awaiting: AWAITING_CUSTOMER_IO_WORDING,
    waitsForClientUse: true,
    partnerNote:
      'Do not send during the advisor-only period. Rewrite the client-ready scripts to remove "devastating" as fear framing and references to how much an LTC event can cost ($500K-$2M+)',
  },
  'campaign-intake-pending': {
    disposition: 'modify',
    approval: 'awaiting-review',
    awaiting: AWAITING_CUSTOMER_IO_WORDING,
    waitsForClientUse: true,
    partnerNote:
      'Trigger only after client use is permitted. Rewrite embedded follow-up templates: remove high-ROI/fear-based phrasing; use neutral reminder language.',
  },
  'campaign-plan-ready': {
    disposition: 'as-is',
    approval: 'approved',
    waitsForClientUse: true,
    partnerNote:
      'Enable only after client-facing release. How does agent calendar display if not integrated into their acct?',
  },
  'campaign-client-intake-guide': {
    disposition: 'as-is',
    approval: 'awaiting-review',
    awaiting: 'NYL SMRU review of both email variants and the attached/linked planning guides',
    waitsForClientUse: true,
    partnerNote:
      'Enable only after client-facing release.  Submit both email variants and the actual attached/linked guides through SMRU; underlying guide language must be approved, not just the wrapper email.',
  },
  'campaign-ten-minute-tracker': {
    disposition: 'disable',
    partnerNote:
      'If advisor has questions, they will reach out per the pilot training.  Also implies that a client planning session occurred solely because the advisor spent 10 minutes on a profile.',
  },
  'campaign-ten-plans-gift': {
    disposition: 'disable',
    partnerNote:
      'Disable the gift/milestone email and gift fulfillment for the NYL pilot. Usage can still be measured without an advisor gift.',
  },
  'campaign-email-connection-reminder': { disposition: 'disable', partnerNote: 'To discuss' },
  'campaign-weekly-digest': {
    disposition: 'disable',
    partnerNote:
      'Disable.  The current reference is too incomplete to approve.  What is part of the digest as would need to be reviewed by SMRU.',
  },
  'campaign-cancellation-confirmation': {
    disposition: 'disable',
    partnerNote:
      'Disable. Advisor subscription cancellation is not part of the NYL pilot operating model.',
  },
  'campaign-post-cancellation': {
    disposition: 'disable',
    partnerNote: 'Disable the post-cancellation retention/upsell drip.',
  },
  'campaign-last-chance': {
    disposition: 'disable',
    partnerNote: 'Disable the subscription-expiration / retention marketing message.',
  },
  'campaign-extended-last-chance': {
    disposition: 'disable',
    partnerNote:
      'Disable the cancellation-retention campaign, including “real client reps” / promotional retention language.',
  },
};

/** Opens a locked body with the "Hi {first name}," line the intake invite's default body starts with. */
export function withClientGreeting(
  clientFirstName: string | null | undefined,
  body: string,
): string {
  return clientFirstName ? `Hi ${clientFirstName},\n\n${body}` : body;
}
